<template>
  <v-row dense class="mx-0">
    <v-col cols="12" md="4">
      <v-card elevation="0" rounded="lg" class="pa-2 pt-2 h-full">
        <div class="d-flex align-center mb-0 px-1">
          <v-icon color="red" class="mr-2">mdi-circle</v-icon>
          <h6 class="text-subtitle-1 font-weight-bold mb-0">
            Acontecendo Agora
          </h6>
        </div>
        <v-skeleton-loader v-if="loading" type="list-item@3" />
        <div
          v-else-if="!timeline?.happeningNow?.length"
          class="text-caption text--secondary mt-2 px-1"
        >
          Nenhum evento acontecendo hoje.
        </div>
        <v-list v-else dense class="pa-0">
          <v-list-item
            v-for="party in timeline.happeningNow"
            :key="party.id"
            class="px-0"
          >
            <div v-if="party.cover" class="mr-2">
              <v-img
                :src="party.cover"
                :aspect-ratio="16 / 9"
                height="40"
                :width="40 * (16 / 9)"
                class="rounded"
              />
            </div>
            <v-list-item-content>
              <v-list-item-title class="font-weight-medium">
                {{ party.name }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ party.Organization?.name }}
              </v-list-item-subtitle>
              <v-list-item-subtitle class="text-caption">
                <v-chip x-small class="mr-1" color="blue lighten-4">
                  {{ party.counters?.ticketsToday || 0 }} ingressos hoje
                </v-chip>
                <v-chip x-small color="purple lighten-4">
                  {{ party.counters?.barTokensToday || 0 }} fichas hoje
                </v-chip>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </v-col>

    <v-col cols="12" md="4">
      <v-card elevation="0" rounded="lg" class="pa-4" height="100%">
        <div class="d-flex align-center mb-4">
          <v-icon color="primary" class="mr-2">mdi-calendar-clock</v-icon>
          <h6 class="text-subtitle-1 font-weight-bold mb-0">
            Próximos Eventos
          </h6>
        </div>
        <v-skeleton-loader v-if="loading" type="list-item@3" />
        <div
          v-else-if="!timeline?.upcoming?.length"
          class="text-caption text--secondary mt-2 px-1"
        >
          Nenhum evento futuro encontrado.
        </div>
        <v-list v-else dense class="pa-0">
          <v-list-item
            v-for="party in timeline.upcoming"
            :key="party.id"
            class="px-0"
          >
            <div v-if="party.cover" class="mr-2">
              <v-img
                :src="party.cover"
                :aspect-ratio="16 / 9"
                height="48"
                :width="48 * (16 / 9)"
                class="rounded"
              />
            </div>
            <v-list-item-content>
              <v-list-item-title class="font-weight-medium">
                {{ party.name }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ party.Organization?.name }}
              </v-list-item-subtitle>
              <v-list-item-subtitle class="text-caption primary--text">
                {{ daysUntilLabel(party.date) }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <span class="text-caption">{{ party.date | date("DD/MM") }}</span>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card>
    </v-col>

    <v-col cols="12" md="4">
      <v-card elevation="0" rounded="lg" class="pa-4" height="100%">
        <div class="d-flex align-center mb-4">
          <v-icon color="teal" class="mr-2">mdi-new-box</v-icon>
          <h6 class="text-subtitle-1 font-weight-bold mb-0">
            Cadastrados Recentemente
          </h6>
        </div>
        <v-skeleton-loader v-if="loading" type="list-item@3" />
        <div
          v-else-if="!timeline?.recentlyCreated?.length"
          class="text-caption text--secondary mt-2 px-1"
        >
          Nenhum evento cadastrado recentemente.
        </div>
        <v-list v-else dense class="pa-0">
          <v-list-item
            v-for="party in timeline.recentlyCreated"
            :key="party.id"
            class="px-0"
          >
            <div v-if="party.cover" class="mr-2">
              <v-img
                :src="party.cover"
                :aspect-ratio="16 / 9"
                height="48"
                :width="48 * (16 / 9)"
                class="rounded"
              />
            </div>
            <v-list-item-content>
              <v-list-item-title class="font-weight-medium">
                {{ party.name }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ party.Organization?.name }} ·
                {{ party.Organization?.Platform?.name }}
              </v-list-item-subtitle>
              <v-list-item-subtitle class="text-caption">
                Evento em {{ party.date | date("DD/MM/YYYY") }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <span class="text-caption">{{
                formatDate(party.createdAt)
              }}</span>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import moment from "moment";

export default {
  name: "DashboardTimeline",
  props: {
    timeline: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },
  methods: {
    daysUntilLabel(date) {
      if (!date) return "";
      const days = moment(date)
        .startOf("day")
        .diff(moment().startOf("day"), "days");
      if (days === 0) return "Hoje";
      if (days === 1) return "Daqui a 1 dia";
      return `Daqui a ${days} dias`;
    },
    formatDate(date) {
      // if date is today, return "Hoje"
      if (moment(date).isSame(moment(), "day")) return "Hoje";
      // ontem
      if (moment(date).isSame(moment().subtract(1, "day"), "day"))
        return "Ontem";

      // humanize the date (há 2 dias)
      return moment(date).fromNow();
    },
  },
};
</script>
